import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { CreditCard, Lock, CheckCircle2 } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { apiClient } from '@/services/api';
import { cn } from '@/utils/cn';

interface Props {
  /** Quando termina de tokenizar com sucesso. Caller decide o próximo passo. */
  onSuccess: (data: { card_brand: string; card_last4: string }) => void;
  /** Texto do botão de envio (ex: "Salvar cartão", "Ativar plano agora"). */
  submitLabel?: string;
}

/**
 * Form de cartão de crédito que tokeniza via nosso backend → Asaas.
 *
 * IMPORTANTE: os dados do cartão SÓ existem em memória do React. Não vão pra
 * localStorage, nem pra logs, nem pra estado global. Após o submit bem-sucedido,
 * o componente limpa todos os campos.
 *
 * Em produção, a recomendação PCI é tokenizar direto do browser via SDK do
 * Asaas (não passar pelo nosso backend). Como nosso backend só repassa pra
 * Asaas e nunca persiste, o risco é controlado, mas vale considerar a migração
 * pro SDK JS do Asaas se o volume crescer.
 */
export function CreditCardForm({ onSuccess, submitLabel = 'Salvar cartão' }: Props) {
  const [holderName, setHolderName] = useState('');
  const [number, setNumber] = useState('');
  const [expiry, setExpiry] = useState(''); // MM/AA
  const [ccv, setCcv] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [addressNumber, setAddressNumber] = useState('');

  const mutation = useMutation({
    mutationFn: async () => {
      const [mm, yy] = expiry.split('/');
      const res = await apiClient.addCreditCard({
        holder_name: holderName.trim(),
        number: number.replace(/\s/g, ''),
        expiry_month: mm,
        expiry_year: yy,
        ccv,
        postal_code: postalCode.replace(/\D/g, ''),
        address_number: addressNumber.trim(),
      });
      return res.data as { card_brand: string; card_last4: string };
    },
    onSuccess: (data) => {
      // Limpa o form imediatamente — não queremos dados de cartão em memória
      // depois do uso. O onSuccess do parent recebe só metadados não-sensíveis.
      setHolderName(''); setNumber(''); setExpiry(''); setCcv('');
      setPostalCode(''); setAddressNumber('');
      onSuccess(data);
    },
  });

  function formatCardNumber(v: string) {
    return v.replace(/\D/g, '').slice(0, 19).replace(/(\d{4})(?=\d)/g, '$1 ');
  }
  function formatExpiry(v: string) {
    const digits = v.replace(/\D/g, '').slice(0, 4);
    return digits.length > 2 ? `${digits.slice(0, 2)}/${digits.slice(2)}` : digits;
  }
  function formatCep(v: string) {
    const digits = v.replace(/\D/g, '').slice(0, 8);
    return digits.length > 5 ? `${digits.slice(0, 5)}-${digits.slice(5)}` : digits;
  }

  const valid =
    holderName.trim().length >= 3 &&
    number.replace(/\s/g, '').length >= 13 &&
    /^\d{2}\/\d{2,4}$/.test(expiry) &&
    /^\d{3,4}$/.test(ccv) &&
    postalCode.replace(/\D/g, '').length === 8 &&
    addressNumber.trim().length > 0;

  return (
    <form
      onSubmit={(e) => { e.preventDefault(); if (valid) mutation.mutate(); }}
      autoComplete="on"
      className="space-y-3"
    >
      <div className="flex items-center gap-2 text-xs text-muted-foreground mb-1">
        <Lock className="h-3.5 w-3.5 text-emerald-500" />
        Dados criptografados e enviados direto ao gateway. Não armazenamos seu cartão.
      </div>

      <div>
        <label className="block text-xs font-semibold text-muted-foreground mb-1">Número do cartão</label>
        <div className="relative">
          <input
            type="text"
            inputMode="numeric"
            autoComplete="cc-number"
            placeholder="0000 0000 0000 0000"
            value={number}
            onChange={(e) => setNumber(formatCardNumber(e.target.value))}
            className="w-full bg-background border-2 border-border rounded-lg pl-10 pr-3 py-2.5 font-mono text-sm focus:border-brand-600 focus:outline-none"
          />
          <CreditCard className="h-4 w-4 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2" />
        </div>
      </div>

      <div>
        <label className="block text-xs font-semibold text-muted-foreground mb-1">Nome impresso no cartão</label>
        <input
          type="text"
          autoComplete="cc-name"
          placeholder="MARIA SILVA"
          value={holderName}
          onChange={(e) => setHolderName(e.target.value.toUpperCase())}
          className="w-full bg-background border-2 border-border rounded-lg px-3 py-2.5 text-sm focus:border-brand-600 focus:outline-none"
        />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-semibold text-muted-foreground mb-1">Validade (MM/AA)</label>
          <input
            type="text"
            inputMode="numeric"
            autoComplete="cc-exp"
            placeholder="12/27"
            value={expiry}
            onChange={(e) => setExpiry(formatExpiry(e.target.value))}
            className="w-full bg-background border-2 border-border rounded-lg px-3 py-2.5 text-sm focus:border-brand-600 focus:outline-none"
          />
        </div>
        <div>
          <label className="block text-xs font-semibold text-muted-foreground mb-1">CVV</label>
          <input
            type="text"
            inputMode="numeric"
            autoComplete="cc-csc"
            placeholder="123"
            value={ccv}
            onChange={(e) => setCcv(e.target.value.replace(/\D/g, '').slice(0, 4))}
            className="w-full bg-background border-2 border-border rounded-lg px-3 py-2.5 text-sm focus:border-brand-600 focus:outline-none"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-semibold text-muted-foreground mb-1">CEP do titular</label>
          <input
            type="text"
            inputMode="numeric"
            autoComplete="postal-code"
            placeholder="00000-000"
            value={postalCode}
            onChange={(e) => setPostalCode(formatCep(e.target.value))}
            className="w-full bg-background border-2 border-border rounded-lg px-3 py-2.5 text-sm focus:border-brand-600 focus:outline-none"
          />
        </div>
        <div>
          <label className="block text-xs font-semibold text-muted-foreground mb-1">Número do endereço</label>
          <input
            type="text"
            placeholder="123"
            value={addressNumber}
            onChange={(e) => setAddressNumber(e.target.value)}
            className="w-full bg-background border-2 border-border rounded-lg px-3 py-2.5 text-sm focus:border-brand-600 focus:outline-none"
          />
        </div>
      </div>

      {mutation.isError && (
        <div className="rounded-lg bg-red-50 border border-red-200 p-3 text-sm text-red-700">
          Não conseguimos validar o cartão. Confira os dados e tente novamente.
          {(mutation.error as any)?.response?.data?.detail && (
            <div className="text-xs mt-1 opacity-75">{(mutation.error as any).response.data.detail}</div>
          )}
        </div>
      )}

      {mutation.isSuccess && (
        <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-3 text-sm text-emerald-700 flex items-center gap-2">
          <CheckCircle2 className="h-4 w-4" />
          Cartão validado com sucesso!
        </div>
      )}

      <Button
        type="submit"
        variant="gradient"
        className={cn('btn-gradient-glow w-full', !valid && 'opacity-50')}
        disabled={!valid}
        loading={mutation.isPending}
      >
        {submitLabel}
      </Button>
    </form>
  );
}
